import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CourseLite } from "../../types/course.response";
import { getCourseKey } from "../../utils/courseUtils";
import Loading from "../loading/Loading";
import UnappliedWarning from "../shared/UnappliedWarning";

interface HeaderCourseChipProps {
    course: CourseLite;
    isApplied: boolean;
    nodeId: string;
    requirementId: string;
    onDelete: (course: CourseLite) => void | Promise<void>;
}

function getChipStyle(course: CourseLite, isApplied: boolean) {
    const status = course.userState.status;
    if (isApplied) {
        if (status === "COMPLETED") return "bg-green-50 text-green-700 border-green-200";
        if (status === "IN_PROGRESS") return "bg-amber-50 text-amber-700 border-amber-200";
        if (status === "PLANNED") return "bg-blue-50 text-blue-700 border-blue-200";
        return "bg-gray-100 text-gray-500 border-gray-200";
    }
    if (status === "COMPLETED") return "bg-gray-100 text-gray-400 border-green-300";
    if (status === "IN_PROGRESS") return "bg-gray-100 text-gray-400 border-amber-300";
    if (status === "PLANNED") return "bg-gray-100 text-gray-400 border-blue-300";
    return "bg-gray-100 text-gray-400 border-gray-200";
}

export default function HeaderCourseChip({ course, isApplied, nodeId, requirementId, onDelete }: HeaderCourseChipProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    const unappliedBinding = !isApplied
        ? course.userState.requirementBindings.find(
              b => b.requirementId === requirementId && (b.status === 'UNAPPLIED' || b.status === 'FORCE_UNAPPLIED')
          )
        : undefined;
    const courseKey = getCourseKey(course);

    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id: `chip-drag-${courseKey}`,
        data: { course, isApplied, nodeId },
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `chip-drop-${courseKey}`,
        data: { course, nodeId, type: "chip" },
        disabled: !isApplied,
    });

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDeleting(true);
        try {
            await onDelete(course);
        } finally {
            setIsDeleting(false);
        }
    };

    const setRefs = (el: HTMLElement | null) => {
        setDragRef(el);
        setDropRef(el);
    };

    return (
        <span
            ref={setRefs}
            {...listeners}
            {...attributes}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium cursor-grab active:cursor-grabbing transition-all ${getChipStyle(course, isApplied)} ${isDragging ? "opacity-40 scale-95" : ""} ${isOver && isApplied ? "ring-2 ring-blue-400 ring-offset-1" : ""} ${isDeleting ? "opacity-50" : ""}`}
        >
            {course.courseInfo.subject} {course.courseInfo.number}
            {unappliedBinding && (
                <UnappliedWarning binding={unappliedBinding} iconClassName="w-3 h-3" />
            )}
            <button
                onClick={handleDelete}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isDeleting}
                className="ml-0.5 rounded-full hover:bg-black/10 transition-colors p-0.5 -mr-1 disabled:cursor-not-allowed"
            >
                {isDeleting ? (
                    <Loading size="sm" />
                ) : (
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M3 3l6 6M9 3l-6 6" />
                    </svg>
                )}
            </button>
        </span>
    );
}
